import React from 'react';
import { MOBILE_DASHBOARD_ROLES, hasRole } from './constants/roles';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import '@shopify/polaris/build/esm/styles.css';
import { AppProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import { AuthProvider, LoginForm, ProtectedRoute, useAuth } from './AuthComponent';
import { StoreProvider, useStore, STORE_KEYS, STORE_DISPLAY_NAMES, isValidStoreSlug } from './StoreContext';
import AppShell from './AppShell';
import OrderDetail from './OrderDetail';
import Settings from './Settings';
import Reporting from './Reporting';
import Activity from './Activity';
import Products from './Products';
import StockProducts from './StockProducts';
import Collections from './Collections';
import Inbox from './Inbox';
import ProductDetail from './ProductDetail';
import PressingPortal from './PressingPortal';
import PressingPortalLogin from './PressingPortalLogin';
import PickingPortal from './PickingPortal';
import ManagerBarcodePrint from './ManagerBarcodePrint';
import QCPortal from './QCPortal';
import StorePickupPortal from './StorePickupPortal';
import BulkShippingPortal from './BulkShippingPortal';
import ShopifyOpenRedirect from './ShopifyOpenRedirect';
import WarehousePortal from './WarehousePortal';
import Overstock from './overstock/Overstock';
import Help from './Help';
import RankingsPortal from './rankings-portal/RankingsPortal';
import RankingsDisplay from './rankings-display/RankingsDisplay';
import { I18nProvider } from './i18n/I18nContext';
import OrdersTanStack from './orders-v2/OrdersTanStack';
import AppVersionBanner from './AppVersionBanner';

const PORTAL_ROUTES = {
  picker:        '/picking-portal/active',
  presser:       '/pressing-portal/active',
  packing:       '/packing-portal/active',
  store:         '/store-pickup-portal/active',
  warehouse:     '/warehouse-portal/home',
  bulk_shipping: '/bulk-shipping-portal/active',
};

// When pressing mode is active, lock to the fullscreen pressing portal.
function PressingModeGuard({ children }) {
  if (sessionStorage.getItem('pressing_admin_token')) {
    const session = JSON.parse(sessionStorage.getItem('presser_session') || '{}');
    if (!session.portal_type) return <Navigate to="/pressing-portal/login" replace />;
    return <Navigate to={PORTAL_ROUTES[session.portal_type]} replace />;
  }
  return children;
}

// Sidebar pressing-portal order URLs redirect to the fullscreen equivalent when in pressing mode.
// PortalDailySummary rows include ?store=X&loc=Y so the fullscreen bootstrap
// can auto-resolve which of a multi-assignment PIN user's locations to open at.
// Preserve `location.search` through the redirect — dropping it makes the
// bootstrap fall back to "Please open this order from the search page."
function PressingOrderGuard({ children }) {
  const { orderId } = useParams();
  const { search } = useLocation();
  if (sessionStorage.getItem('pressing_admin_token')) {
    return <Navigate to={`/pressing-portal/active/orders/${orderId}${search}`} replace />;
  }
  return children;
}

// Sidebar picking-portal order URLs redirect to the fullscreen equivalent when in picking mode.
function PickingOrderGuard({ children }) {
  const { orderId } = useParams();
  const { search } = useLocation();
  const session = JSON.parse(sessionStorage.getItem('presser_session') || '{}');
  if (sessionStorage.getItem('pressing_admin_token') && session.portal_type === 'picker') {
    return <Navigate to={`/picking-portal/active/orders/${orderId}${search}`} replace />;
  }
  return children;
}

// Sidebar packing-portal order URLs redirect to the fullscreen equivalent when in packing mode.
function PackingOrderGuard({ children }) {
  const { orderId } = useParams();
  const { search } = useLocation();
  const session = JSON.parse(sessionStorage.getItem('presser_session') || '{}');
  if (sessionStorage.getItem('pressing_admin_token') && session.portal_type === 'packing') {
    return <Navigate to={`/packing-portal/active/orders/${orderId}${search}`} replace />;
  }
  return children;
}

// Guards routes under /store/:storeSlug/*. Validates slug format, checks user access,
// and renders a friendly 403 if the user doesn't have access to that store.
function StoreSlugGuard({ children }) {
  const { storeSlug } = useParams();
  const { accessibleStores } = useStore();
  const { user } = useAuth();

  if (!isValidStoreSlug(storeSlug)) {
    return <Navigate to="/" replace />;
  }
  const isAllStores = hasRole(user, ['admin']) || hasRole(user, ['customer_service']) || hasRole(user, ['finance']);
  if (storeSlug === 'all' && !isAllStores) {
    return <NoStoreAccess slug={storeSlug} />;
  }
  if (!accessibleStores.includes(storeSlug)) {
    return <NoStoreAccess slug={storeSlug} />;
  }
  return children;
}

function NoStoreAccess({ slug }) {
  const name = STORE_DISPLAY_NAMES[slug] || slug;
  return (
    <div style={{ padding: 32, maxWidth: 600, margin: '64px auto', textAlign: 'center' }}>
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>No access to {name}</h1>
      <p style={{ color: '#6d7175', marginBottom: 20 }}>
        You don't have access to the <strong>{name}</strong> store. If you think this is a mistake, contact an administrator.
      </p>
      <a href="/" style={{ color: '#2c6ecb', textDecoration: 'none' }}>← Back to dashboard</a>
    </div>
  );
}

// Determines the user's default store from (1) localStorage hint, (2) user.locations, (3) 'soccerpost'.
function pickDefaultStore(user) {
  const isAllStores = hasRole(user, ['admin']) || hasRole(user, ['customer_service']) || hasRole(user, ['finance']);
  const userStores = [...new Set((user?.locations || []).map(l => l.store_key).filter(Boolean))];
  const pool = isAllStores ? STORE_KEYS : (userStores.length > 0 ? userStores : ['soccerpost']);
  const saved = localStorage.getItem('ops_active_store');
  if (saved && (pool.includes(saved) || (saved === 'all' && (isAllStores || userStores.length > 1)))) return saved;
  return pool[0];
}

// Redirects a legacy URL like /orders or /orders/:id to the scoped equivalent under the
// user's default store. Preserves path segments after the legacy prefix, plus query string.
// Wrapped in ProtectedRoute by the caller so the user is loaded before we read locations.
function LegacyRedirect({ legacyPrefix }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || !user) return null;
  const defaultStore = pickDefaultStore(user);
  // Compute the portion of the pathname after the legacy prefix.
  const suffix = location.pathname.startsWith(legacyPrefix)
    ? location.pathname.slice(legacyPrefix.length)
    : '';
  const target = `/store/${defaultStore}${legacyPrefix}${suffix}${location.search}`;
  return <Navigate to={target} replace />;
}

// The "/" root path is mobile-aware:
// - On mobile with a dashboard-eligible role, render AppShell so MobileDashboard
//   shows its tile menu (AppShell checks pathname === '/' internally).
// - Otherwise redirect to the user's default /store/{slug}/orders.
// 2026-09-11: crm + finance removed. MobileDashboard's only content is the
// "Enter Portals" button (PORTAL_LAUNCH_ROLES), so those roles landed on an
// empty navy screen on every phone (David Montanjees, CRM, Sep 8). They now
// go straight to the Orders list, exactly as on a desktop-width screen.
// The list itself lives in constants/roles.js (shared with AppShell + LoginForm).
function RootRoute() {
  const { user, loading } = useAuth();
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  if (loading || !user) return null;
  if (isMobile && hasRole(user, MOBILE_DASHBOARD_ROLES)) {
    // AppShell renders MobileDashboard automatically when pathname === '/'
    return <AppShell />;
  }
  const defaultStore = pickDefaultStore(user);
  return <Navigate to={`/store/${defaultStore}/orders`} replace />;
}

// Reads user from AuthContext and provides store context to the rest of the tree
function StoreProviderWrapper({ children }) {
  const { user } = useAuth();
  return <StoreProvider user={user}>{children}</StoreProvider>;
}

// Store-scoped element: wraps a page in PressingModeGuard + ProtectedRoute + StoreSlugGuard + AppShell.
function StoreRoute({ children, settingsAccess = false, pressingGuard = true }) {
  const inner = (
    <ProtectedRoute settingsAccess={settingsAccess}>
      <StoreSlugGuard>
        <AppShell>{children}</AppShell>
      </StoreSlugGuard>
    </ProtectedRoute>
  );
  return pressingGuard ? <PressingModeGuard>{inner}</PressingModeGuard> : inner;
}

function App() {
  return (
    <AppProvider i18n={enTranslations}>
      <I18nProvider>
      <AuthProvider>
        <Router>
          <StoreProviderWrapper>
            <AppVersionBanner />
            <Routes>
              <Route path="/login" element={<LoginForm />} />

              {/* Rankings TV display — public token-gated leaderboard. Lives OUTSIDE
                  AppShell/ProtectedRoute: no sidebar, no auth check. */}
              <Route path="/rankings/display/:token" element={<RankingsDisplay />} />

              {/* ──────────────── New path-scoped routes: /store/:storeSlug/* ──────────────── */}
              <Route path="/store/:storeSlug" element={<Navigate to="orders" replace />} />
              <Route path="/store/:storeSlug/orders" element={<StoreRoute><OrdersTanStack /></StoreRoute>} />
              <Route path="/store/:storeSlug/orders/:orderId" element={<StoreRoute><OrderDetail /></StoreRoute>} />
              <Route path="/store/:storeSlug/products/stock" element={<StoreRoute><StockProducts /></StoreRoute>} />
              <Route path="/store/:storeSlug/products/inventory" element={<StoreRoute><Products /></StoreRoute>} />
              <Route path="/store/:storeSlug/products/collections" element={<StoreRoute><Collections /></StoreRoute>} />
              <Route path="/store/:storeSlug/products/collection/:collectionId" element={<StoreRoute><Collections /></StoreRoute>} />
              <Route path="/store/:storeSlug/inbox" element={<StoreRoute><Inbox /></StoreRoute>} />
              <Route path="/store/:storeSlug/products" element={<Navigate to="inventory" replace />} />
              <Route path="/store/:storeSlug/products/:productId" element={<StoreRoute><ProductDetail /></StoreRoute>} />
              <Route path="/store/:storeSlug/pressing-portal" element={<StoreRoute><PressingPortal /></StoreRoute>} />
              <Route path="/store/:storeSlug/pressing-portal/orders/:orderId" element={
                <PressingOrderGuard>
                  <ProtectedRoute>
                    <StoreSlugGuard>
                      <AppShell><PressingPortal /></AppShell>
                    </StoreSlugGuard>
                  </ProtectedRoute>
                </PressingOrderGuard>
              } />
              <Route path="/store/:storeSlug/picking-portal" element={
                <ProtectedRoute>
                  <StoreSlugGuard>
                    <AppShell><PickingPortal /></AppShell>
                  </StoreSlugGuard>
                </ProtectedRoute>
              } />
              <Route path="/store/:storeSlug/picking-portal/orders/:orderId" element={
                <PickingOrderGuard>
                  <ProtectedRoute>
                    <StoreSlugGuard>
                      <AppShell><PickingPortal /></AppShell>
                    </StoreSlugGuard>
                  </ProtectedRoute>
                </PickingOrderGuard>
              } />
              <Route path="/store/:storeSlug/packing-portal" element={
                <ProtectedRoute>
                  <StoreSlugGuard>
                    <AppShell><QCPortal /></AppShell>
                  </StoreSlugGuard>
                </ProtectedRoute>
              } />
              <Route path="/store/:storeSlug/packing-portal/orders/:orderId" element={
                <PackingOrderGuard>
                  <ProtectedRoute>
                    <StoreSlugGuard>
                      <AppShell><QCPortal /></AppShell>
                    </StoreSlugGuard>
                  </ProtectedRoute>
                </PackingOrderGuard>
              } />
              <Route path="/store/:storeSlug/reporting" element={<StoreRoute><Reporting /></StoreRoute>} />
              <Route path="/store/:storeSlug/reporting/:reportSlug" element={<StoreRoute><Reporting /></StoreRoute>} />

              {/* ──────────────── Legacy → scoped redirects ──────────────── */}
              <Route path="/" element={<ProtectedRoute><RootRoute /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/orders" /></ProtectedRoute>} />
              <Route path="/orders/:orderId" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/orders" /></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/products" /></ProtectedRoute>} />
              <Route path="/products/:productId" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/products" /></ProtectedRoute>} />
              <Route path="/pressing-portal" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/pressing-portal" /></ProtectedRoute>} />
              <Route path="/pressing-portal/orders/:orderId" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/pressing-portal" /></ProtectedRoute>} />
              <Route path="/picking-portal" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/picking-portal" /></ProtectedRoute>} />
              <Route path="/picking-portal/orders/:orderId" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/picking-portal" /></ProtectedRoute>} />
              <Route path="/packing-portal" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/packing-portal" /></ProtectedRoute>} />
              <Route path="/packing-portal/orders/:orderId" element={<ProtectedRoute><LegacyRedirect legacyPrefix="/packing-portal" /></ProtectedRoute>} />

              {/* ──────────────── Global (unscoped) routes ──────────────── */}
              <Route path="/activity" element={<ProtectedRoute><AppShell><Activity /></AppShell></ProtectedRoute>} />
              <Route path="/activity/:reportSlug" element={<ProtectedRoute><AppShell><Activity /></AppShell></ProtectedRoute>} />
              <Route path="/settings" element={<PressingModeGuard><ProtectedRoute settingsAccess><AppShell><Settings /></AppShell></ProtectedRoute></PressingModeGuard>} />
              {/* Manager Override barcode printout — new tab, no shell,
                  auto-prints on load. Route path shape matches the "Print"
                  link in Settings → Portal Users. */}
              <Route path="/settings/manager-barcode/print/:userId" element={<ProtectedRoute><ManagerBarcodePrint /></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute><AppShell><Help /></AppShell></ProtectedRoute>} />
              {/* Rankings — admin-only TV leaderboard token management. RankingsPortal
                  itself also gates on hasRole(user, ['admin']) (belt-and-suspenders). */}
              <Route path="/rankings-portal" element={<ProtectedRoute><AppShell><RankingsPortal /></AppShell></ProtectedRoute>} />

              {/* Pressing Portal active mode — full-screen, PIN-based presser session */}
              <Route path="/pressing-portal/active" element={<ProtectedRoute><PressingPortal fullscreen /></ProtectedRoute>} />
              <Route path="/pressing-portal/active/orders/:orderId" element={<ProtectedRoute><PressingPortal fullscreen /></ProtectedRoute>} />
              <Route path="/pressing-portal/login" element={<PressingPortalLogin />} />
              {/* Picking Portal active mode */}
              <Route path="/picking-portal/active" element={<ProtectedRoute><PickingPortal fullscreen /></ProtectedRoute>} />
              <Route path="/picking-portal/active/orders/:orderId" element={<ProtectedRoute><PickingPortal fullscreen /></ProtectedRoute>} />
              {/* Packing Portal active mode */}
              <Route path="/packing-portal/active" element={<ProtectedRoute><QCPortal fullscreen /></ProtectedRoute>} />
              <Route path="/packing-portal/active/orders/:orderId" element={<ProtectedRoute><QCPortal fullscreen /></ProtectedRoute>} />
              {/* Store Pickup Portal active mode */}
              <Route path="/store-pickup-portal" element={<Navigate to="/pressing-portal/login" replace />} />
              <Route path="/store-pickup-portal/active" element={<ProtectedRoute><StorePickupPortal /></ProtectedRoute>} />
              {/* Console-mode entry: admins / franchise / site_manager / user
                  walk in from the sidebar without entering a portal PIN. The
                  portal builds its session from the operations_users auth user
                  (first_name + locations), and shows a store picker first
                  when the user has assignments across multiple stores. */}
              <Route path="/store-pickup-portal/console" element={<ProtectedRoute><AppShell><StorePickupPortal consoleMode /></AppShell></ProtectedRoute>} />
              <Route path="/bulk-shipping-portal" element={<Navigate to="/pressing-portal/login" replace />} />
              <Route path="/bulk-shipping-portal/active" element={<ProtectedRoute><BulkShippingPortal /></ProtectedRoute>} />

              {/* Bulk Shipping Portal — signed-in console mode (sidebar entry,
                  admin / site_manager / user). Location scope comes from the
                  JWT via /api/locations; ?loc=<shopify_location_id> preselects. */}
              <Route path="/store/:storeSlug/bulk-shipping-portal/active" element={
                <ProtectedRoute>
                  <StoreSlugGuard>
                    <AppShell><BulkShippingPortal consoleMode /></AppShell>
                  </StoreSlugGuard>
                </ProtectedRoute>
              } />

              {/* Warehouse Portal — signed-in console mode (sidebar entry).
                  Per-store path is rendered inside AppShell so the user keeps
                  their normal navigation; the portal reads its location scope
                  from the JWT. */}
              <Route path="/store/:storeSlug/warehouse-portal/*" element={
                <ProtectedRoute>
                  <StoreSlugGuard>
                    <AppShell><WarehousePortal consoleMode /></AppShell>
                  </StoreSlugGuard>
                </ProtectedRoute>
              } />
              {/* Warehouse Portal — PIN kiosk mode. Wildcard splat so the portal owns its own subrouting */}
              <Route path="/warehouse-portal/*" element={<ProtectedRoute><WarehousePortal /></ProtectedRoute>} />

              {/* Overstock (RFID) — signed-in console mode (sidebar entry, gated
                  by user.rfid_enabled). Warehouse scope comes from /api/rfid/config. */}
              <Route path="/store/:storeSlug/overstock" element={
                <ProtectedRoute>
                  <StoreSlugGuard>
                    <AppShell><Overstock /></AppShell>
                  </StoreSlugGuard>
                </ProtectedRoute>
              } />

              {/* Shopify admin "Open in Operations Hub" link (Order Status app admin_link extension) */}
              <Route path="/shopify/open" element={<ProtectedRoute><ShopifyOpenRedirect /></ProtectedRoute>} />
              <Route path="/qc-portal" element={<Navigate to="/packing-portal" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </StoreProviderWrapper>
        </Router>
      </AuthProvider>
      </I18nProvider>
    </AppProvider>
  );
}

export default App;
